import React, { useState, useCallback } from "react";
import { useSafeNavigate } from "../../hooks/useSafeRouter";
import { SafeLink } from "../../ui/SafeLink";
import { Home, ArrowLeft, Clock, RefreshCw, Play, RotateCcw, Bug, X, Copy } from "lucide-react";
import { Section } from "../../ui/section";
import { ScrollContainer } from "../../ui/scroll-container";
import { Button } from "../../ui/button";
import { useQuery } from "@tanstack/react-query";
import { GetUser } from "../../api/queries";
import type { ErrorInfo } from "react";

// Simple Number Memory Game Component
function MemoryGame() {
  const [sequence, setSequence] = useState<number[]>([]);
  const [playerSequence, setPlayerSequence] = useState<number[]>([]);
  const [showSequence, setShowSequence] = useState(false);
  const [level, setLevel] = useState(1);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('404-memory-high-score') || '0');
  });
  const [gameStatus, setGameStatus] = useState<'idle' | 'showing' | 'input' | 'correct' | 'wrong'>('idle');

  const buttons = [1, 2, 3, 4, 5, 6, 7, 8, 9];

  const startGame = useCallback(() => {
    const newSequence = [Math.floor(Math.random() * 9) + 1];
    setSequence(newSequence);
    setPlayerSequence([]);
    setLevel(1);
    setGameStatus('showing');
    setShowSequence(true);

    setTimeout(() => {
      setShowSequence(false);
      setGameStatus('input');
    }, 1500);
  }, []);

  const addNumber = useCallback((num: number) => {
    if (gameStatus !== 'input') return;

    const newPlayerSequence = [...playerSequence, num];
    setPlayerSequence(newPlayerSequence);

    // Check if sequence matches so far
    if (newPlayerSequence[newPlayerSequence.length - 1] !== sequence[newPlayerSequence.length - 1]) {
      setGameStatus('wrong');
      if (level > highScore) {
        setHighScore(level);
        localStorage.setItem('404-memory-high-score', level.toString());
      }
      return;
    }

    // Check if sequence is complete
    if (newPlayerSequence.length === sequence.length) {
      setGameStatus('correct');
      setLevel(prev => prev + 1);

      setTimeout(() => {
        const nextSequence = [...sequence, Math.floor(Math.random() * 9) + 1];
        setSequence(nextSequence);
        setPlayerSequence([]);
        setGameStatus('showing');
        setShowSequence(true);

        setTimeout(() => {
          setShowSequence(false);
          setGameStatus('input');
        }, 1500 + (nextSequence.length * 100));
      }, 1000);
    }
  }, [gameStatus, playerSequence, sequence, level, highScore]);

  return (
    <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-white mb-2">Memory Challenge</h3>
        <div className="flex justify-center gap-4 text-sm text-gray-300">
          <span>Level: {level}</span>
          <span>High Score: {highScore}</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto mb-4">
        {buttons.map((num) => (
          <button
            key={num}
            onClick={() => addNumber(num)}
            disabled={gameStatus !== 'input'}
            className={`
              w-16 h-16 rounded-lg font-bold text-xl transition-all duration-200
              ${showSequence && sequence.includes(num)
                ? 'bg-blue-500 text-white animate-pulse'
                : 'bg-gray-700 hover:bg-gray-600 text-white'
              }
              ${gameStatus !== 'input' ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
              ${playerSequence.includes(num) ? 'ring-2 ring-green-400' : ''}
            `}
          >
            {num}
          </button>
        ))}
      </div>

      <div className="text-center">
        {gameStatus === 'idle' && (
          <div>
            <p className="text-gray-300 text-sm mb-4">
              Remember the sequence and repeat it!
            </p>
            <Button
              onClick={startGame}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Play className="w-4 h-4 mr-2" />
              Start Game
            </Button>
          </div>
        )}

        {gameStatus === 'showing' && (
          <p className="text-blue-400 text-sm animate-pulse">
            Watch the sequence... Level {level}
          </p>
        )}

        {gameStatus === 'input' && (
          <p className="text-green-400 text-sm">
            Repeat the sequence ({playerSequence.length}/{sequence.length})
          </p>
        )}

        {gameStatus === 'correct' && (
          <p className="text-green-400 text-sm animate-pulse">
            Correct! Next level...
          </p>
        )}

        {gameStatus === 'wrong' && (
          <div>
            <p className="text-red-400 text-sm mb-4">
              Wrong sequence! You reached level {level}
            </p>
            <Button
              onClick={startGame}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function NotFound() {
  const navigate = useSafeNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <ScrollContainer snapType="y" snapBehavior="mandatory">
      {/* Main 404 Section */}
      <Section
        fullHeight={true}
        scrollSnap={true}
        variant="dark"
        contentPosition="center"
        padding="xl"
        className="bg-black relative overflow-hidden"
      >
        {/* Animated Background Effects */}
        <div className="absolute inset-0">
          {/* Broken Glass Effect */}
          <div className="absolute inset-0 opacity-30">
            {/* Glass shards */}
            <div className="absolute top-10 left-10 w-20 h-32 bg-gradient-to-br from-white/10 to-transparent border-l border-t border-white/20 transform rotate-12 blur-sm"></div>
            <div className="absolute top-40 right-20 w-16 h-28 bg-gradient-to-br from-white/15 to-transparent border-r border-b border-white/25 transform -rotate-45 blur-sm"></div>
            <div className="absolute bottom-32 left-32 w-24 h-20 bg-gradient-to-br from-white/8 to-transparent border-l border-b border-white/15 transform rotate-25 blur-sm"></div>
            <div className="absolute top-20 right-40 w-12 h-24 bg-gradient-to-br from-white/12 to-transparent border-r border-t border-white/20 transform rotate-75 blur-sm"></div>
            <div className="absolute bottom-20 right-20 w-28 h-16 bg-gradient-to-br from-white/10 to-transparent border-l border-t border-white/18 transform -rotate-30 blur-sm"></div>
            <div className="absolute top-60 left-20 w-14 h-26 bg-gradient-to-br from-white/14 to-transparent border-r border-b border-white/22 transform rotate-60 blur-sm"></div>
            <div className="absolute bottom-40 left-60 w-18 h-22 bg-gradient-to-br from-white/9 to-transparent border-l border-b border-white/16 transform -rotate-15 blur-sm"></div>

            {/* Crack lines */}
            <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-white/20 to-transparent transform rotate-12"></div>
            <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-white/15 to-transparent transform -rotate-25"></div>
            <div className="absolute top-0 left-2/3 w-px h-full bg-gradient-to-b from-transparent via-white/18 to-transparent transform rotate-8"></div>
            <div className="absolute left-0 top-1/3 w-full h-px bg-gradient-to-r from-transparent via-white/12 to-transparent transform rotate-3"></div>
            <div className="absolute left-0 bottom-1/4 w-full h-px bg-gradient-to-r from-transparent via-white/16 to-transparent transform -rotate-2"></div>
          </div>

          {/* Floating particles */}
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-60"></div>
          <div className="absolute top-3/4 right-1/4 w-3 h-3 bg-red-400 rounded-full animate-pulse opacity-40"></div>
          <div className="absolute top-1/2 left-3/4 w-1 h-1 bg-red-600 rounded-full animate-bounce opacity-80"></div>
          <div className="absolute bottom-1/4 left-1/2 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-50"></div>

          {/* Masked window effects with red tones */}
          <div className="absolute top-20 left-20 w-40 h-40 bg-gradient-to-br from-red-600/20 to-red-800/20 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-60 h-60 bg-gradient-to-br from-red-700/20 to-red-900/20 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-gradient-to-br from-red-600/10 to-red-800/10 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          {/* Animated 404 Number with Glitch Effect */}
          <div className="relative mb-8">
            <div className="text-[8rem] md:text-[12rem] lg:text-[16rem] font-black text-gray-800 select-none leading-none opacity-20">
              404
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-4xl md:text-6xl lg:text-8xl font-black text-white animate-pulse relative">
                <span className="relative">
                  404
                  <span className="absolute inset-0 text-red-500 animate-ping opacity-75 transform translate-x-1">404</span>
                  <span className="absolute inset-0 text-blue-500 animate-ping opacity-50 transform -translate-x-1 delay-100">404</span>
                </span>
              </div>
            </div>
          </div>

          {/* Error Message */}
          <div className="mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Lost in the <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-red-600 to-red-700 animate-pulse">Digital Void</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
              The page you're looking for has drifted into the digital cosmos.
              While we search for it, why not try our mini-game below?
            </p>
          </div>

          {/* Mini Game Section */}
          <div className="mb-12">
            <MemoryGame />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button
              onClick={handleGoBack}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl hover:shadow-red-500/25"
            >
              <ArrowLeft className="w-5 h-5" />
              Go Back
            </Button>

            <SafeLink to="/">
              <Button
                variant="outline"
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-gray-600 hover:border-white text-gray-300 hover:text-white hover:bg-white/10 font-semibold rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl backdrop-blur-sm"
              >
                <Home className="w-5 h-5" />
                Go Home
              </Button>
            </SafeLink>
          </div>

          {/* Help Text */}
          <div className="p-6 bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-700">
            <div className="flex items-center justify-center gap-2 text-blue-400 mb-2">
              <Clock className="w-5 h-5" />
              <span className="font-medium">Need immediate assistance?</span>
            </div>
            <p className="text-sm text-gray-400">
              If you believe this is an error, please contact our support team or try refreshing the page.
            </p>
            <Button
              onClick={() => window.location.reload()}
              variant="ghost"
              className="mt-3 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Page
            </Button>
          </div>
        </div>
      </Section>
    </ScrollContainer>
  );
}

// Admin Error Debug Modal Component
function AdminErrorModal({
  error,
  errorInfo,
  onClose,
  isAdministrator,
  isLocalhost
}: {
  error?: Error;
  errorInfo?: ErrorInfo;
  onClose: () => void;
  isAdministrator?: boolean;
  isLocalhost?: boolean;
}) {
  const copyToClipboard = () => {
    const errorText = `
ERROR: ${error?.name || 'Unknown'}
MESSAGE: ${error?.message || 'No message'}
STACK:
${error?.stack || 'No stack trace'}

COMPONENT STACK:
${errorInfo?.componentStack || 'No component stack'}
    `.trim();

    navigator.clipboard.writeText(errorText);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-2xl p-6 max-w-4xl w-full max-h-[80vh] border border-red-700/50 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
              <Bug className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Debug Information</h3>
              <p className="text-sm text-gray-400">
                {isAdministrator ? 'Administrator Access' : 'Localhost Development'} - Error details for debugging
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={copyToClipboard}
              variant="outline"
              size="sm"
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white hover:bg-gray-800"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Error Details */}
        <div className="flex-1 overflow-auto space-y-4">
          {/* Error Name & Message */}
          <div className="bg-gray-800/50 rounded-lg p-4">
            <h4 className="text-red-400 font-semibold mb-2">Error Details</h4>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-400">Name:</span>
                <span className="text-white ml-2 font-mono">{error?.name || 'Unknown'}</span>
              </div>
              <div>
                <span className="text-gray-400">Message:</span>
                <span className="text-white ml-2 font-mono">{error?.message || 'No message'}</span>
              </div>
            </div>
          </div>

          {/* Stack Trace */}
          {error?.stack && (
            <div className="bg-gray-800/50 rounded-lg p-4">
              <h4 className="text-red-400 font-semibold mb-2">Stack Trace</h4>
              <pre className="text-xs text-gray-300 font-mono overflow-auto whitespace-pre-wrap">
                {error.stack}
              </pre>
            </div>
          )}

          {/* Component Stack */}
          {errorInfo?.componentStack && (
            <div className="bg-gray-800/50 rounded-lg p-4">
              <h4 className="text-red-400 font-semibold mb-2">Component Stack</h4>
              <pre className="text-xs text-gray-300 font-mono overflow-auto whitespace-pre-wrap">
                {errorInfo.componentStack}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Failed({ error, errorInfo }: { error?: Error; errorInfo?: ErrorInfo } = {}) {
  const navigate = useSafeNavigate();
  const [showAdminModal, setShowAdminModal] = useState(false);

  // Check if user is Administrator or on localhost
  const { data: userData } = useQuery({
    queryKey: ['user'],
    queryFn: GetUser,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const isAdministrator = userData?.result?.rolls?.includes('Administrators');
  const isLocalhost = window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.') ||
    window.location.hostname.endsWith('.local');

  // Show debug features if user is administrator OR on localhost (for development)
  const canShowDebugInfo = isAdministrator || isLocalhost;

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleShowDebugInfo = () => {
    setShowAdminModal(true);
  };

  const handleCloseDebugModal = () => {
    setShowAdminModal(false);
  };

  return (
    <ScrollContainer snapType="y" snapBehavior="mandatory">
      <Section
        fullHeight={true}
        scrollSnap={true}
        variant="dark"
        contentPosition="center"
        padding="xl"
        className="bg-black relative overflow-hidden"
      >
        {/* Animated Background Effects */}
        <div className="absolute inset-0">
          {/* Floating particles */}
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-60"></div>
          <div className="absolute top-3/4 right-1/4 w-3 h-3 bg-orange-500 rounded-full animate-pulse opacity-40"></div>
          <div className="absolute top-1/2 left-3/4 w-1 h-1 bg-yellow-500 rounded-full animate-bounce opacity-80"></div>
          <div className="absolute bottom-1/4 left-1/2 w-2 h-2 bg-red-600 rounded-full animate-ping opacity-50"></div>

          {/* Masked window effects - more intense for error */}
          <div className="absolute top-20 left-20 w-40 h-40 bg-gradient-to-br from-red-600/30 to-orange-600/30 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-60 h-60 bg-gradient-to-br from-orange-600/30 to-red-600/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-gradient-to-br from-red-600/20 to-yellow-600/20 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        <div className="max-w-3xl mx-auto text-center relative z-10">
          {/* Error Icon with Glitch Effect */}
          <div className="relative mb-8">
            <div className="w-32 h-32 mx-auto bg-gradient-to-br from-red-900/50 to-orange-900/50 backdrop-blur-sm rounded-full flex items-center justify-center border border-red-700/50">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center animate-pulse">
                  <span className="text-white text-3xl font-bold">!</span>
                </div>
                {/* Glitch effects */}
                <div className="absolute inset-0 w-16 h-16 bg-gradient-to-br from-red-400 to-red-500 rounded-full flex items-center justify-center opacity-50 animate-ping transform translate-x-1">
                  <span className="text-white text-3xl font-bold">!</span>
                </div>
                <div className="absolute inset-0 w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center opacity-30 animate-ping transform -translate-x-1 delay-100">
                  <span className="text-white text-3xl font-bold">!</span>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            System <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 animate-pulse">Malfunction</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed mb-12">
            We encountered an unexpected error in the digital matrix.
            Our cyber engineers are debugging the quantum flux capacitors as we speak.
          </p>

          {/* Error Details with Futuristic Design */}
          <div className="mb-12 p-6 bg-gray-900/50 backdrop-blur-sm rounded-xl border border-red-700/50 max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center justify-center gap-2">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              Diagnostic Report
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            </h3>
            <div className="space-y-3 text-left text-gray-300">
              <div className="flex items-center gap-3 p-2 rounded bg-gray-800/50">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Refreshing the page might resolve the issue</span>
              </div>
              <div className="flex items-center gap-3 p-2 rounded bg-gray-800/50">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Check your network connection status</span>
              </div>
              <div className="flex items-center gap-3 p-2 rounded bg-gray-800/50">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Contact support if the anomaly persists</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl hover:shadow-red-500/25"
            >
              <RefreshCw className="w-5 h-5" />
              Restart System
            </Button>

            <Button
              onClick={handleGoBack}
              variant="outline"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-gray-600 hover:border-white text-gray-300 hover:text-white hover:bg-white/10 font-semibold rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl backdrop-blur-sm"
            >
              <ArrowLeft className="w-5 h-5" />
              Go Back
            </Button>

            {/* Admin Debug Button */}
            {canShowDebugInfo && (error || errorInfo) && (
              <Button
                onClick={handleShowDebugInfo}
                variant="outline"
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-yellow-600 hover:border-yellow-400 text-yellow-300 hover:text-yellow-200 hover:bg-yellow-900/20 font-semibold rounded-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl backdrop-blur-sm"
              >
                <Bug className="w-5 h-5" />
                Debug Info
              </Button>
            )}
          </div>

          {/* Support Contact */}
          <div className="p-6 bg-red-900/20 backdrop-blur-sm rounded-xl border border-red-700/50">
            <div className="flex items-center justify-center gap-2 text-red-400 mb-2">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              <span className="font-medium">Emergency Protocol Active</span>
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            </div>
            <p className="text-sm text-gray-400">
              Critical system failure detected. Immediate technical assistance required.{' '}
              <SafeLink to="/contact" className="text-red-400 hover:text-red-300 underline font-medium transition-colors">
                Contact Mission Control
              </SafeLink>
            </p>
          </div>
        </div>

        {/* Admin Debug Modal */}
        {showAdminModal && (
          <AdminErrorModal
            error={error}
            errorInfo={errorInfo}
            onClose={handleCloseDebugModal}
            isAdministrator={isAdministrator}
            isLocalhost={isLocalhost}
          />
        )}
      </Section>
    </ScrollContainer>
  );
}

export { NotFound, Failed };